import { useCallback, useState } from 'react'
import { sendConfirmation } from '../services/awin-service'
import { datadogRum } from '@datadog/browser-rum'
import { clearCustomerJourneyCookie } from '@cinch-labs/customer-journey-cookies'

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    AWIN: any
  }
}

const advertiserId = 30_879
const currencyCode = 'GBP'

export const useAwin = () => {
  const [loading, setLoading] = useState(false)
  const [requestSent, setRequestSent] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  const sendAwinConfirmation = useCallback(
    async (bookingRef: string, bookingValue: string, cookie: Cookie) => {
      if (requestSent) {
        return
      }
      setLoading(true)
      setError(null)

      try {
        const testMode = process.env['STAGE_NAME'] !== 'production'

        const isApp =
          typeof window !== 'undefined' &&
          window.navigator?.userAgent.includes('cinchapp')
        const platformType = isApp ? 'app' : 'web'

        datadogRum.addAction('Call AWIN', {
          bookingRef,
          bookingValue,
          cookie,
          platformType,
        })

        callAWINConversionTag(
          cookie,
          bookingRef,
          bookingValue,
          platformType,
          testMode,
        )

        createFallbackTrackingPixel(
          cookie,
          bookingRef,
          bookingValue,
          platformType,
          testMode,
        )

        const success = await sendConfirmation({
          orderTotalInPence: Number.parseFloat(bookingValue) * 100,
          channel: cookie.channel,
          orderId: bookingRef,
          commissionGroup: cookie.commissionGroup,
          voucherCode: cookie.voucher,
          testMode,
          userId: 'Cinch-Servicing-User',
          clickId: cookie.awc ?? '',
          platformType,
        })

        clearCustomerJourneyCookie()

        if (success) {
          setRequestSent(true)
          setError(null)
        } else {
          setError(new Error('Request failed to send'))
        }
      } catch (error) {
        datadogRum.addError(new Error('Call AWIN Error'), {
          bookingRef,
          bookingValue,
          cookie,
          error: error,
        })
        setError(error as Error)
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  return {
    sendAwinConfirmation,
    loading,
    error,
  }
}

function callAWINConversionTag(
  cookie: Cookie,
  bookingRef: string,
  bookingValue: string,
  platformType: string,
  testMode: boolean,
) {
  if (
    typeof window !== 'undefined' &&
    window.AWIN &&
    window.AWIN.Tracking &&
    window.AWIN.Tracking.run
  ) {
    window.AWIN.Tracking.Sale = {}
    window.AWIN.Tracking.Sale.amount = bookingValue
    window.AWIN.Tracking.Sale.channel = cookie.channel
    window.AWIN.Tracking.Sale.orderRef = bookingRef
    window.AWIN.Tracking.Sale.parts = `${cookie.commissionGroup}:${bookingValue}`
    window.AWIN.Tracking.Sale.voucher = cookie.voucher
    window.AWIN.Tracking.Sale.currency = currencyCode
    window.AWIN.Tracking.Sale.custom = [platformType]
    window.AWIN.Tracking.Sale.test = testMode ? `1` : `0`
    window.AWIN.Tracking.run()
  }
}

function createFallbackTrackingPixel(
  cookie: Cookie,
  bookingRef: string,
  bookingValue: string,
  platformType: string,
  testMode: boolean,
) {
  const img = document.createElement('img')
  const data = {
    tt: 'ns',
    tv: '2',
    merchant: advertiserId,
    amount: bookingValue,
    ch: cookie.channel,
    parts: `${cookie.commissionGroup}:${bookingValue}`,
    vc: cookie.voucher,
    cr: currencyCode,
    ref: bookingRef,
    p1: platformType,
    testmode: testMode,
  }
  const queryParams = objectToQueryParams(data)
  const baseURL = `https://www.awin1.com/sread.img?`
  img.src = baseURL + queryParams
  document.body.append(img)
}

const objectToQueryParams = (obj: Record<string, unknown>) =>
  Object.keys(obj)
    .map((key) => `${key}=${encodeURIComponent(obj[key] as string)}`)
    .join('&')

type Cookie = {
  commissionGroup: string
  channel: string
  awc: string | null
  voucher: string
}
